import { tickToRate } from '@chainflip/utils/tickMath';
import type { UTCDate } from '@date-fns/utc';
import { Card, FancyDollarDisplay } from '@/shared/components';
import { type StrategyStatsFragment } from '@/shared/graphql/generated/reporting/graphql';
import {
  ChartIcon,
  CoinsIcon,
  DistanceIcon,
  DollarIcon,
  PercentageIcon,
  TrendingUpIcon,
} from '@/shared/icons/large';
import {
  apyToText,
  chainflipAssetMap,
  getTimeframeOrFormattedDateLabels,
  formatUsdValue,
} from '@/shared/utils';
import {
  templateKeySchema,
  type StrategyTemplate,
  type TradingStrategy,
} from '@/shared/utils/strategies';

export const StrategyCards = ({
  startDate,
  endDate,
  stats,
  isStatsLoading,
  ...props
}: {
  startDate: UTCDate;
  endDate: UTCDate;
  stats: StrategyStatsFragment | null | undefined;
  isStatsLoading: boolean;
} & ({ strategy: TradingStrategy | undefined } | { template: StrategyTemplate | undefined })) => {
  const strategy = 'strategy' in props ? props.strategy : undefined;
  const { templateKey, baseAsset, quoteAsset, reportingData } =
    ('strategy' in props ? props.strategy : props.template) ?? {};

  const { minBuyTick, maxBuyTick, minSellTick, maxSellTick } = templateKey
    ? templateKeySchema.parse(templateKey)
    : {
        minBuyTick: undefined,
        maxBuyTick: undefined,
        minSellTick: undefined,
        maxSellTick: undefined,
      };

  const baseAssetAmount =
    'strategy' in props ? props.strategy?.baseLiquidity : props.template?.totalBaseLiquidity;
  const quoteAssetAmount =
    'strategy' in props ? props.strategy?.quoteLiquidity : props.template?.totalQuoteLiquidity;
  const baseAssetAmountUsd =
    'strategy' in props ? props.strategy?.baseLiquidityUsd : props.template?.totalBaseLiquidityUsd;
  const quoteAssetAmountUsd =
    'strategy' in props
      ? props.strategy?.quoteLiquidityUsd
      : props.template?.totalQuoteLiquidityUsd;
  const isClosed = strategy?.status === 'CLOSED';

  let pnlUsd;
  let filledVolumeUsd;
  if (isClosed) {
    pnlUsd = strategy.reportingData?.pnlUsd;
    filledVolumeUsd = strategy.reportingData?.filledVolumeUsd;
  } else if (stats) {
    pnlUsd = Number(stats.totalPnlValueUsd);
    filledVolumeUsd = Number(stats.totalFilledVolumeValueUsd);
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      <Card
        icon={<TrendingUpIcon className="text-cf-light-2" />}
        title="Total PnL"
        tooltip={
          isClosed
            ? 'Estimated net returns for this strategy, including profit and losses.'
            : 'Estimated net returns for this strategy during this period, including profit and losses.'
        }
        footer={isClosed ? 'All time' : getTimeframeOrFormattedDateLabels([startDate, endDate])}
        loading={isStatsLoading}
      >
        <div className="flex h-full items-center text-16">
          <FancyDollarDisplay value={formatUsdValue(pnlUsd ?? 0, undefined, true)} />
        </div>
      </Card>
      <Card
        icon={<ChartIcon className="text-cf-light-2" />}
        title="Filled Volume"
        tooltip={
          isClosed
            ? 'Total value (in USD) of all filled trades for this strategy.'
            : 'Total value (in USD) of all filled trades for this strategy during this period.'
        }
        footer={isClosed ? 'All time' : getTimeframeOrFormattedDateLabels([startDate, endDate])}
        loading={isStatsLoading}
      >
        <div className="flex h-full items-center text-16">
          <FancyDollarDisplay value={formatUsdValue(filledVolumeUsd ?? 0)} />
        </div>
      </Card>
      {!isClosed && (
        <Card
          icon={<DollarIcon className="text-cf-light-2" />}
          title={'strategy' in props ? 'Strategy Value' : 'Strategies Value'}
          tooltip="Total value (in USD) deployed in this strategy."
          footer="Current"
          loading={baseAssetAmountUsd == null || quoteAssetAmountUsd == null}
        >
          <div className="flex h-full items-center text-16">
            <FancyDollarDisplay
              value={formatUsdValue((baseAssetAmountUsd ?? 0) + (quoteAssetAmountUsd ?? 0))}
            />
          </div>
        </Card>
      )}
      {!isClosed && (
        <Card
          icon={<CoinsIcon className="text-cf-light-2" />}
          title="Deployed Liquidity"
          tooltip="The amount of each asset deployed in this strategy."
          loading={!baseAssetAmount || !quoteAssetAmount}
        >
          <div className="flex h-full flex-col gap-y-2 text-16">
            <div className="flex flex-row items-center gap-x-2">
              <span>
                {baseAssetAmount?.toFormatted()}{' '}
                {baseAsset && chainflipAssetMap[baseAsset].assetAndChainName}
              </span>
              <span className="text-12 text-cf-light-2">{formatUsdValue(baseAssetAmountUsd)}</span>
            </div>
            <div className="flex flex-row items-center gap-x-2">
              <span>
                {quoteAssetAmount?.toFormatted()}{' '}
                {quoteAsset && chainflipAssetMap[quoteAsset].assetAndChainName}
              </span>
              <span className="text-12 text-cf-light-2">{formatUsdValue(quoteAssetAmountUsd)}</span>
            </div>
          </div>
        </Card>
      )}
      {!isClosed && (
        <Card
          icon={<PercentageIcon className="text-cf-light-2" />}
          title="Estimated APY"
          tooltip="Estimated annual yield projected from recent fill data and allocation."
          footer="Current"
          loading={!reportingData}
        >
          <div className="flex h-full items-center text-16">
            {apyToText(reportingData?.apyPercent ?? 0)}
          </div>
        </Card>
      )}
      <Card
        icon={<DistanceIcon className="text-cf-light-2" />}
        title="Spread"
        tooltip="The difference between the buy and sell prices around the mid-market rate in basis points."
        loading={!baseAsset || minBuyTick === undefined}
      >
        <div className="flex h-full items-center text-16">
          {minBuyTick !== undefined &&
            maxBuyTick !== undefined &&
            minSellTick !== undefined &&
            maxSellTick !== undefined &&
            baseAsset !== undefined &&
            templateKey !== undefined && (
              <div className="flex flex-col gap-y-2">
                <div>
                  <span className="text-cf-light-2">Buy:</span>{' '}
                  {tickToRate(minBuyTick, baseAsset).toFixed(4)} -{' '}
                  {tickToRate(maxBuyTick, baseAsset).toFixed(4)}
                </div>
                <div>
                  <span className="text-cf-light-2">Sell: </span>
                  {tickToRate(minSellTick, baseAsset).toFixed(4)} -{' '}
                  {tickToRate(maxSellTick, baseAsset).toFixed(4)}
                </div>
              </div>
            )}
        </div>
      </Card>
    </div>
  );
};
